/**
 * @file pages/Blogs.jsx
 * @description Blog listing page with stunning grid layout, category filtering,
 * search, reading time badges, hover animations, and real API integration.
 * Falls back to sample data when the API is unavailable.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from "../Components/Layouts/Header";
import Footer from "../Components/Layouts/Footer";
import { Link } from 'react-router-dom';
import { getAllBlogs } from '../services/api';
import { sampleBlogs } from '../data/blogData';
import { FiClock, FiHeart, FiEye, FiSearch, FiArrowRight } from 'react-icons/fi';

const extractFirstImage = (html) => {
  if (!html || typeof html !== 'string') return null;
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return match?.[1] || null;
};

const BlogCard = ({ blog, featured = false }) => {
  const imageUrl = blog.featuredImage?.url || blog.image || extractFirstImage(blog.content) || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80';
  const slug = blog.slug || blog._id;
  const readTime = blog.readingTime || blog.readTime || '3';
  const authorName = typeof blog.author === 'object' ? blog.author?.name : (blog.author || 'Nikaba Travels');

  return (
    <Link
      to={`/blogs/${slug}`}
      className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1.5 flex flex-col ${featured ? 'md:col-span-2 md:flex-row' : ''}`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden ${featured ? 'md:w-1/2 h-64 md:h-auto' : 'h-56'}`}>
        <img
          src={imageUrl}
          alt={blog.title}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="px-3 py-1 text-xs font-bold text-white bg-[#243185]/90 backdrop-blur-sm rounded-full shadow-lg">
            {blog.category || 'Travel Guide'}
          </span>
        </div>
        <div className="absolute bottom-4 left-4 flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
          <span className="flex items-center gap-1 text-white/90 text-xs bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
            <FiEye className="w-3 h-3" /> {blog.viewCount || 0}
          </span>
          <span className="flex items-center gap-1 text-white/90 text-xs bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
            <FiHeart className="w-3 h-3" /> {blog.likesCount || 0}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className={`p-6 flex flex-col flex-1 ${featured ? 'md:w-1/2 md:p-8 md:justify-center' : ''}`}>
        <div className="flex items-center gap-3 text-sm text-gray-400 mb-3">
          <span className="flex items-center gap-1">
            <FiClock className="w-3.5 h-3.5" />
            {readTime} min read
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span>{new Date(blog.createdAt || blog.publishDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>

        <h2 className={`font-bold text-gray-900 mb-3 leading-tight line-clamp-2 group-hover:text-[#243185] transition-colors duration-300 ${featured ? 'text-2xl md:text-3xl' : 'text-xl'}`}>
          {blog.title}
        </h2>

        <p className={`text-gray-500 mb-5 line-clamp-3 flex-grow leading-relaxed ${featured ? 'text-base' : 'text-sm'}`}>
          {blog.excerpt}
        </p>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-xs font-bold">
              {authorName?.charAt(0)?.toUpperCase() || 'N'}
            </div>
            <span className="text-sm font-medium text-gray-600">{authorName}</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[#243185] text-sm font-semibold group-hover:gap-2 transition-all duration-300">
            Read <FiArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 9 };
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const response = await getAllBlogs(params);
      const data = response.data?.data || response.data;

      if (data?.blogs?.length > 0) {
        setBlogs(data.blogs);
        setCategories(data.categories || []);
        setTotalPages(data.pagination?.pages || 1);
      } else {
        // Fallback to sample data
        setBlogs(sampleBlogs);
        setCategories([...new Set(sampleBlogs.map(b => b.category))]);
      }
    } catch (error) {
      console.error('Blog fetch error:', error);
      setBlogs(sampleBlogs);
      setCategories([...new Set(sampleBlogs.map(b => b.category))]);
    } finally {
      setLoading(false);
    }
  }, [page, selectedCategory, searchTerm]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Travel Blog | Nikaba Travels - Stories, Tips & Guides</title>
        <meta name="description" content="Explore inspiring travel stories, expert tips, destination guides, and cultural insights from Nikaba Travels. Your go-to resource for planning unforgettable journeys across India and beyond." />
        <link rel="canonical" href="https://nikabatravels.com/blogs" />
      </Helmet>

      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f1b4d] via-[#1a2a6c] to-[#243185] text-white pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1920&q=80')] bg-cover bg-center opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1b4d]/80 to-[#243185]/90" />
        </div>
        {/* Floating decorative elements */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-blue-200 text-sm font-medium mb-6 border border-white/10">
              ✈️ Travel Stories & Guides
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1]">
              Explore the World
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-cyan-300 mt-2">
                Through Our Stories
              </span>
            </h1>
            <p className="text-lg text-blue-100/80 max-w-2xl mx-auto leading-relaxed">
              Discover hidden gems, expert travel tips, and inspiring stories from destinations across India and beyond.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mt-10">
            <div className="relative">
              <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search destinations, guides, tips..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="w-full pl-14 pr-6 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-blue-200/50 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:bg-white/15 transition-all text-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2 flex items-center gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => { setSelectedCategory('all'); setPage(1); }}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === 'all'
                ? 'bg-[#243185] text-white shadow-md'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All Posts
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setSelectedCategory(cat); setPage(1); }}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-[#243185] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Blog Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                <div className="h-56 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-5 bg-gray-200 rounded w-full" />
                  <div className="h-5 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-3 bg-gray-200 rounded w-4/5" />
                </div>
              </div>
            ))}
          </div>
        ) : blogs.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog, index) => (
                <BlogCard
                  key={blog._id || index}
                  blog={blog}
                  featured={index === 0 && page === 1}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-14">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  ← Previous
                </button>
                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${
                        page === i + 1
                          ? 'bg-[#243185] text-white shadow-md'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-5 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No articles found</h3>
            <p className="text-gray-500">Try adjusting your search or category filter.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Blogs;
